import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/auth';
import { getAcquisitionHeaders } from '../utils/acquisition';
import { useSubscription } from '../contexts/SubscriptionContext';
import { formatKr } from '../utils/formatKr';
import { useTranslation } from '../hooks/useTranslation';

/**
 * /abonnement — subscription start page with legally required consent
 * capture. The three checkboxes map 1:1 to the consent types the backend
 * enforces in POST /api/subscription/start; the button stays disabled until
 * all three are checked. Approval itself happens in Vipps — we redirect to
 * vippsConfirmationUrl and land back on /abonnement/bekreft.
 */

// Bump when any consent wording changes — stored on the consent records.
const CONSENT_VERSION = 'v2-2026-09';

export default function Subscription() {
  const navigate = useNavigate();
  const { status, priceOre, hasAccess, refresh } = useSubscription();
  const { t } = useTranslation();

  const [consents, setConsents] = useState({
    terms: false,
    withdrawal: false,
    health: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null); // null | 'vipps' | 'generic'

  const allChecked = consents.terms && consents.withdrawal && consents.health;
  // null until GET /status has answered — a skeleton stands in for the
  // amount; there is no hardcoded fallback, so the only price a user ever
  // sees is the one the backend would draft at (its quotedPriceOre rule).
  // formatKr is the shared øre formatter: whole kroner "299 kr", otherwise
  // "1,51 kr" — never rounded to whole kroner.
  const priceLabel = priceOre == null ? null : formatKr(priceOre);

  const toggle = (key) =>
    setConsents(prev => ({ ...prev, [key]: !prev[key] }));

  const handleStart = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post('/subscription/start', {
        consents: [
          { type: 'terms', version: CONSENT_VERSION },
          { type: 'withdrawal_immediate_delivery', version: CONSENT_VERSION },
          { type: 'health_data_article9', version: CONSENT_VERSION }
        ]
      }, {
        // Landing-URL acquisition record (utils/acquisition.js) — only
        // matters if GET /profile did not already create the Profile.
        headers: getAcquisitionHeaders()
      });
      const data = await response.json();
      if (data.vippsConfirmationUrl) {
        // Off to Vipps — the agreement is approved there, and the redirect
        // brings the user back to /abonnement/bekreft.
        window.location.assign(data.vippsConfirmationUrl);
        return;
      }
      setError('generic');
      setSubmitting(false);
    } catch (err) {
      if (err.status === 409) {
        // Already active (e.g. approved in Vipps earlier) — sync and move on.
        await refresh();
        navigate('/', { replace: true });
        return;
      }
      setError(err.status === 502 ? 'vipps' : 'generic');
      setSubmitting(false);
    }
  };

  // Already active (deep link or gate off): no form, just point onwards.
  if (status === 'active') {
    // An ACTIVE agreement whose access has lapsed (renewal failed, grace
    // exhausted — hasAccess is the server verdict from /status) must not be
    // told it is "already active": say what happened and where to fix it.
    const activeCopy = hasAccess
      ? t('subscription.alreadyActive')
      : t('subscription.activeButLapsed');
    return (
      <div className="max-w-md mx-auto py-8">
        <h1 className="text-2xl font-semibold text-sage mb-3">{t('subscription.title')}</h1>
        <div className="bg-background rounded-2xl border border-border-light shadow-sm p-6">
          <p className={`mb-4 ${hasAccess ? 'text-text-primary' : 'text-error-700'}`}>{activeCopy}</p>
          <Link to="/account/abonnement" className="text-primary-600 font-medium hover:underline">
            {t('subscription.manageLink')}
          </Link>
        </div>
      </div>
    );
  }

  const checkboxes = [
    {
      key: 'terms',
      content: (
        <>
          {t('subscription.consentTermsPrefix')}
          <a
            href="/salgsvilkar"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 underline hover:text-primary-700"
          >
            {t('subscription.consentTermsLink')}
          </a>
        </>
      )
    },
    {
      key: 'withdrawal',
      content: <>{t('subscription.consentWithdrawal')}</>
    },
    {
      key: 'health',
      content: (
        <>
          {t('subscription.consentHealthText')}{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 underline hover:text-primary-700"
          >
            {t('subscription.consentHealthLink')}
          </a>
          .
        </>
      )
    }
  ];

  return (
    // Full-bleed warm page background (#F0EEE6) behind the padded app
    // container; the negative margins undo the container's p-4 pt-[70px]
    // (mobile) / p-8 (md+) padding.
    <div className="-mx-4 -mb-4 -mt-[70px] md:-m-8 min-h-screen bg-background-card px-4 pt-[86px] pb-12 md:pt-14">
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-semibold text-sage mb-2">{t('subscription.title')}</h1>
        <p className="text-text-secondary mb-6">{t('subscription.intro')}</p>

        {/* Price card */}
        <div className="bg-background rounded-2xl border border-border-light shadow-sm p-6 mb-6">
          <div className="flex items-baseline gap-2">
            {priceLabel === null ? (
              <span className="inline-block h-9 w-24 rounded bg-gray-200 animate-pulse" aria-hidden="true"></span>
            ) : (
              <span className="text-3xl font-semibold text-text-primary">{priceLabel}</span>
            )}
            <span className="text-text-secondary">{t('subscription.perMonth')}</span>
          </div>
          <p className="text-sm text-text-muted mt-1">{t('subscription.vatIncluded')}</p>
          <p className="text-sm text-text-secondary mt-3">{t('subscription.cancelAnytime')}</p>
        </div>

        {/* Consent checkboxes — all required, unchecked by default */}
        <div className="bg-background rounded-2xl border border-border-light shadow-sm divide-y divide-border-light mb-6">
          {checkboxes.map(({ key, content }) => (
            <label
              key={key}
              className="flex items-start gap-3 p-4 min-h-[48px] cursor-pointer"
            >
              <input
                type="checkbox"
                checked={consents[key]}
                onChange={() => toggle(key)}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-border-dark text-sage-600 accent-[#6B8F72] focus:ring-sage-400"
              />
              <span className="text-sm text-text-primary leading-relaxed">{content}</span>
            </label>
          ))}
        </div>

        {error && (
          <div className="bg-error-50 border border-error-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-error-700 mb-3">
              {error === 'vipps' ? t('subscription.errorVipps') : t('subscription.errorGeneric')}
            </p>
            <button
              onClick={handleStart}
              disabled={!allChecked || submitting}
              className="text-sm font-medium text-error-700 underline disabled:opacity-50"
            >
              {t('subscription.retry')}
            </button>
          </div>
        )}

        {/* Vipps orange is deliberate here — it is a Vipps action */}
        <button
          onClick={handleStart}
          disabled={!allChecked || submitting}
          className="w-full min-h-[52px] rounded-xl bg-[#FF5B24] hover:bg-[#e6511f] text-white font-semibold text-base px-6 py-3.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
              {t('subscription.starting')}
            </>
          ) : (
            t('subscription.startButton')
          )}
        </button>

        {/* The gate hides the sidebar, so data rights need a visible path */}
        <p className="text-sm text-text-muted text-center mt-6">
          {t('subscription.accountHint')}{' '}
          <Link to="/account" className="underline hover:text-text-secondary">
            {t('subscription.accountHintLink')}
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
